import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { API_URL, getValidToken } from './utils/authUtils';
import AdminRouteGuard from './components/AdminRouteGuard';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';

const BACKEND_URL = API_URL;

// Color palette for charts
const CHART_COLORS = {
  blue: '#3B82F6',
  yellow: '#F59E0B',
  green: '#10B981',
  purple: '#8B5CF6',
  red: '#EF4444',
  indigo: '#6366F1',
  pink: '#EC4899',
  teal: '#14B8A6'
};

const AdminCompanyDashboardStats = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('company_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [cycleYear, setCycleYear] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(true);

  // Print report handler
  const handlePrint = () => {
    // Ensure analytics are visible for printing
    setShowAnalytics(true);
    // Short delay to let state update render before printing
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = getValidToken();

      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/company-dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(response.data.companies || []);
      setCycleYear(response.data.cycle_year);
      setGeneratedAt(response.data.generated_at);
      setError('');
    } catch (err) {
      console.error('Error fetching company stats:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Please log in as an admin.');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError('Failed to load company statistics');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Calculate analytics data
  const analyticsData = useMemo(() => {
    if (stats.length === 0) return null;

    // Helper to get top N by a field
    const getTopN = (arr, field, n = 6) => {
      return [...arr]
        .sort((a, b) => b[field] - a[field])
        .slice(0, n)
        .map(c => ({
          name: c.company_name.length > 20 ? c.company_name.substring(0, 18) + '...' : c.company_name,
          fullName: c.company_name,
          value: c[field]
        }));
    };

    // Calculate engagement (Total Matches - Potential = students who engaged)
    const withEngagement = stats.map(c => ({
      ...c,
      engaged: c.total_matches - c.potential_count,
      engagement_rate: c.total_matches > 0 ? ((c.total_matches - c.potential_count) / c.total_matches * 100) : 0,
      conversion_rate: c.total_matches > 0 ? (c.applied_count / c.total_matches * 100) : 0,
      // True favorite-to-apply rate: % of favoriters who also applied (uses actual overlap from backend)
      favorite_to_apply: c.favorited_count > 0 ? ((c.overlap_count || 0) / c.favorited_count * 100) : 0,
      overlap_count: c.overlap_count || 0
    }));

    // Top by Total Matches
    const topByMatches = getTopN(stats, 'total_matches');

    // Top by Applied
    const topByApplied = getTopN(stats, 'applied_count');

    // Top by Favorited
    const topByFavorited = getTopN(stats, 'favorited_count');

    // Top by Engagement (Total Matches - Potential)
    const topByEngagement = [...withEngagement]
      .sort((a, b) => b.engaged - a.engaged)
      .slice(0, 6)
      .map(c => ({
        name: c.company_name.length > 20 ? c.company_name.substring(0, 18) + '...' : c.company_name,
        fullName: c.company_name,
        value: c.engaged,
        rate: c.engagement_rate.toFixed(1)
      }));

    // Highest Engagement Rate (among companies with 10+ matches)
    const topByEngagementRate = [...withEngagement]
      .filter(c => c.total_matches >= 10)
      .sort((a, b) => b.engagement_rate - a.engagement_rate)
      .slice(0, 6)
      .map(c => ({
        name: c.company_name.length > 20 ? c.company_name.substring(0, 18) + '...' : c.company_name,
        fullName: c.company_name,
        value: parseFloat(c.engagement_rate.toFixed(1)),
        matches: c.total_matches
      }));

    // Highest Conversion Rate (Applied / Total Matches, min 10 matches)
    const topByConversion = [...withEngagement]
      .filter(c => c.total_matches >= 10)
      .sort((a, b) => b.conversion_rate - a.conversion_rate)
      .slice(0, 6)
      .map(c => ({
        name: c.company_name.length > 20 ? c.company_name.substring(0, 18) + '...' : c.company_name,
        fullName: c.company_name,
        value: parseFloat(c.conversion_rate.toFixed(1)),
        applied: c.applied_count,
        matches: c.total_matches
      }));

    // Highest Favorite-to-Apply Rate (min 5 favorited)
    // This shows what % of students who favorited the company also applied
    const topByFavoriteConversion = [...withEngagement]
      .filter(c => c.favorited_count >= 5)
      .sort((a, b) => b.favorite_to_apply - a.favorite_to_apply)
      .slice(0, 6)
      .map(c => ({
        name: c.company_name.length > 20 ? c.company_name.substring(0, 18) + '...' : c.company_name,
        fullName: c.company_name,
        value: parseFloat(c.favorite_to_apply.toFixed(1)),
        overlap: c.overlap_count,  // Actual count of students who did both
        favorited: c.favorited_count
      }));

    // Highest Untapped Potential (most Potential students)
    const topByPotential = getTopN(stats, 'potential_count');

    // Companies with Zero Engagement
    const zeroEngagement = stats
      .filter(c => c.total_matches > 0 && c.applied_count === 0 && c.favorited_count === 0)
      .sort((a, b) => b.total_matches - a.total_matches)
      .slice(0, 6)
      .map(c => ({
        name: c.company_name.length > 20 ? c.company_name.substring(0, 18) + '...' : c.company_name,
        fullName: c.company_name,
        value: c.total_matches
      }));

    return {
      topByMatches,
      topByApplied,
      topByFavorited,
      topByEngagement,
      topByEngagementRate,
      topByConversion,
      topByFavoriteConversion,
      topByPotential,
      zeroEngagement
    };
  }, [stats]);

  // Filter companies by search term
  const filteredStats = stats.filter(company =>
    company.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort companies
  const sortedStats = [...filteredStats].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <span className="text-gray-400 ml-1">↕</span>;
    }
    return <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // Calculate totals
  const totals = sortedStats.reduce((acc, company) => ({
    total_matches: acc.total_matches + company.total_matches,
    applied_count: acc.applied_count + company.applied_count,
    favorited_count: acc.favorited_count + company.favorited_count,
    potential_count: acc.potential_count + company.potential_count,
    overlap_count: acc.overlap_count + (company.overlap_count || 0),
  }), { total_matches: 0, applied_count: 0, favorited_count: 0, potential_count: 0, overlap_count: 0 });

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Company', 'Recruiters', 'Total Matches', 'Applied', 'Favorited', 'Overlap (Both)', 'Potential', 'Engaged', 'Engagement Rate %', 'Conversion Rate %', 'Fav-to-Apply %'];
    const rows = sortedStats.map(company => {
      const engaged = company.total_matches - company.potential_count;
      const engagementRate = company.total_matches > 0 ? (engaged / company.total_matches * 100).toFixed(1) : 0;
      const conversionRate = company.total_matches > 0 ? (company.applied_count / company.total_matches * 100).toFixed(1) : 0;
      const favToApplyRate = company.favorited_count > 0 ? ((company.overlap_count || 0) / company.favorited_count * 100).toFixed(1) : 0;
      return [
        company.company_name,
        company.recruiter_count,
        company.total_matches,
        company.applied_count,
        company.favorited_count,
        company.overlap_count || 0,
        company.potential_count,
        engaged,
        engagementRate,
        conversionRate,
        favToApplyRate
      ];
    });

    // Add totals row
    const totalEngaged = totals.total_matches - totals.potential_count;
    const totalFavToApply = totals.favorited_count > 0 ? (totals.overlap_count / totals.favorited_count * 100).toFixed(1) : 0;
    rows.push([
      'TOTAL',
      '',
      totals.total_matches,
      totals.applied_count,
      totals.favorited_count,
      totals.overlap_count,
      totals.potential_count,
      totalEngaged,
      totals.total_matches > 0 ? (totalEngaged / totals.total_matches * 100).toFixed(1) : 0,
      totals.total_matches > 0 ? (totals.applied_count / totals.total_matches * 100).toFixed(1) : 0,
      totalFavToApply
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `company-dashboard-stats-${cycleYear || 'current'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Chart component
  const AnalyticsChart = ({ title, subtitle, data, color, valueLabel = 'Count', isPercentage = false }) => {
    if (!data || data.length === 0) {
      return (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">{title}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{subtitle}</p>
          <div className="h-48 flex items-center justify-center text-gray-400 dark:text-gray-500">
            No data available
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">{title}</h4>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{subtitle}</p>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 5, right: 45, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} domain={isPercentage ? [0, 100] : [0, 'auto']} />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 10 }}
                width={80}
              />
              <Tooltip
                formatter={(value) => [isPercentage ? `${value}%` : value, valueLabel]}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                contentStyle={{
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={color} />
                ))}
                <LabelList
                  dataKey="value"
                  position="right"
                  formatter={(value) => isPercentage ? `${value}%` : value}
                  style={{ fontSize: 10, fill: '#374151', fontWeight: 500 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  return (
    <AdminRouteGuard>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          /* Reset background colors for printing */
          body, .bg-gray-50, .bg-white, .bg-gray-100, .dark\\:bg-gray-900, .dark\\:bg-gray-800, .dark\\:bg-gray-700 {
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /* Hide elements that shouldn't print */
          .print\\:hidden {
            display: none !important;
          }

          /* Show search bar area but hide the input */
          input[type="text"] {
            display: none !important;
          }

          /* Keep analytics section expanded */
          .print-always-show {
            display: block !important;
          }

          /* Page setup */
          @page {
            size: landscape;
            margin: 0.5in;
          }

          /* Ensure charts print with colors */
          .recharts-wrapper {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /* Table styling for print */
          table {
            font-size: 10px !important;
            width: 100% !important;
          }

          th, td {
            padding: 4px 8px !important;
          }

          /* Ensure colored badges print */
          .bg-yellow-100, .bg-green-100, .bg-purple-100 {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /* Chart containers should stay a reasonable size */
          .recharts-responsive-container {
            height: 120px !important;
          }

          /* Grid adjustments for print */
          .md\\:grid-cols-3 {
            grid-template-columns: repeat(3, 1fr) !important;
          }

          /* Hide mobile cards */
          .md\\:hidden {
            display: none !important;
          }

          /* Show desktop table */
          .hidden.md\\:block {
            display: block !important;
          }

          /* Page break controls */
          .shadow {
            box-shadow: none !important;
            page-break-inside: avoid;
          }

          /* Ensure text is dark for printing */
          .text-gray-900, .dark\\:text-white, .dark\\:text-gray-300 {
            color: #111827 !important;
          }

          .text-gray-500, .text-gray-600, .dark\\:text-gray-400 {
            color: #6B7280 !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Company Dashboard Statistics
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Aggregated student matching stats for each company
                  {cycleYear && ` • Cycle ${cycleYear}`}
                </p>
              </div>
              <div className="flex items-center gap-3 print:hidden">
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print / PDF
                </button>
                <button
                  onClick={exportToCSV}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                >
                  Export CSV
                </button>
                <Link
                  to="/admin"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  ← Back to Admin
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-lg">
              {error}
            </div>
          )}

          {/* Analytics Section */}
          {!loading && analyticsData && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
              <button
                onClick={() => setShowAnalytics(!showAnalytics)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
              >
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Analytics & Insights
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Top performers and key metrics across all companies
                  </p>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-500 transform transition-transform ${showAnalytics ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showAnalytics && (
                <div className="px-6 pb-6">
                  {/* Row 1: Volume Metrics */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                      Volume Leaders
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <AnalyticsChart
                        title="Top 6 by Total Matches"
                        subtitle="Companies with most eligible students"
                        data={analyticsData.topByMatches}
                        color={CHART_COLORS.blue}
                        valueLabel="Total Matches"
                      />
                      <AnalyticsChart
                        title="Top 6 by Applied"
                        subtitle="Most students with NACME profile updates this cycle"
                        data={analyticsData.topByApplied}
                        color={CHART_COLORS.yellow}
                        valueLabel="Applied"
                      />
                      <AnalyticsChart
                        title="Top 6 by Favorited"
                        subtitle="Most students expressing interest"
                        data={analyticsData.topByFavorited}
                        color={CHART_COLORS.green}
                        valueLabel="Favorited"
                      />
                    </div>
                  </div>

                  {/* Row 2: Engagement Metrics */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                      Engagement Leaders
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <AnalyticsChart
                        title="Top 6 by Total Engagement"
                        subtitle="Most students who applied OR favorited"
                        data={analyticsData.topByEngagement}
                        color={CHART_COLORS.indigo}
                        valueLabel="Engaged Students"
                      />
                      <AnalyticsChart
                        title="Highest Engagement Rate"
                        subtitle="% of matches who engaged (min 10 matches)"
                        data={analyticsData.topByEngagementRate}
                        color={CHART_COLORS.teal}
                        valueLabel="Engagement Rate"
                        isPercentage={true}
                      />
                      <AnalyticsChart
                        title="Highest Conversion Rate"
                        subtitle="% of matches who applied (min 10 matches)"
                        data={analyticsData.topByConversion}
                        color={CHART_COLORS.pink}
                        valueLabel="Conversion Rate"
                        isPercentage={true}
                      />
                    </div>
                  </div>

                  {/* Row 3: Opportunity Metrics */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                      Opportunities & Insights
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <AnalyticsChart
                        title="Favorite-to-Apply Conversion"
                        subtitle="% of students who favorited AND applied (min 5 fav)"
                        data={analyticsData.topByFavoriteConversion}
                        color={CHART_COLORS.purple}
                        valueLabel="Conversion Rate"
                        isPercentage={true}
                      />
                      <AnalyticsChart
                        title="Highest Untapped Potential"
                        subtitle="Most matches with no engagement yet"
                        data={analyticsData.topByPotential}
                        color={CHART_COLORS.red}
                        valueLabel="Potential Students"
                      />
                      <AnalyticsChart
                        title="Zero Engagement Alert"
                        subtitle="Companies with matches but no applicants/favorites"
                        data={analyticsData.zeroEngagement}
                        color="#6B7280"
                        valueLabel="Total Matches (0 engaged)"
                      />
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {totals.total_matches > 0
                            ? ((totals.total_matches - totals.potential_count) / totals.total_matches * 100).toFixed(1)
                            : 0}%
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Overall Engagement Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                          {totals.total_matches > 0
                            ? (totals.applied_count / totals.total_matches * 100).toFixed(1)
                            : 0}%
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Overall Conversion Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {stats.filter(c => c.total_matches > 0 && c.applied_count === 0 && c.favorited_count === 0).length}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Companies with Zero Engagement</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {stats.filter(c => c.total_matches > 0 && ((c.total_matches - c.potential_count) / c.total_matches) > 0.5).length}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Companies with 50%+ Engagement</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Search and Info Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6 print:hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:max-w-xs px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {filteredStats.length} companies
                {generatedAt && (
                  <span className="ml-2">
                    • Generated {new Date(generatedAt).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Print-only header with date */}
          <div className="hidden print:block mb-4 text-sm text-gray-600">
            <p>Report generated: {generatedAt ? new Date(generatedAt).toLocaleString() : new Date().toLocaleString()}</p>
            <p>Total companies: {filteredStats.length}</p>
          </div>

          {/* Stats Table */}
          {loading ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading company statistics...</p>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">This may take a moment as we calculate matches for all companies.</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                        onClick={() => handleSort('company_name')}
                      >
                        Company <SortIcon field="company_name" />
                      </th>
                      <th
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                        onClick={() => handleSort('recruiter_count')}
                      >
                        Recruiters <SortIcon field="recruiter_count" />
                      </th>
                      <th
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                        onClick={() => handleSort('total_matches')}
                      >
                        Total Matches <SortIcon field="total_matches" />
                      </th>
                      <th
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                        onClick={() => handleSort('applied_count')}
                      >
                        Applied <SortIcon field="applied_count" />
                      </th>
                      <th
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                        onClick={() => handleSort('favorited_count')}
                      >
                        Favorited <SortIcon field="favorited_count" />
                      </th>
                      <th
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                        onClick={() => handleSort('overlap_count')}
                        title="Students who both Applied AND Favorited"
                      >
                        Overlap <SortIcon field="overlap_count" />
                      </th>
                      <th
                        className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                        onClick={() => handleSort('potential_count')}
                      >
                        Potential <SortIcon field="potential_count" />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedStats.map((company, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {company.company_name}
                          </div>
                          {company.recruiter_names && company.recruiter_names.length > 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {company.recruiter_names.slice(0, 2).join(', ')}
                              {company.recruiter_names.length > 2 && ` +${company.recruiter_names.length - 2} more`}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600 dark:text-gray-300">
                          {company.recruiter_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                            {company.total_matches}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            {company.applied_count}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {company.favorited_count}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                            {company.overlap_count || 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            {company.potential_count}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-gray-100 dark:bg-gray-700 font-semibold">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        TOTAL ({sortedStats.length} companies)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600 dark:text-gray-300">
                        —
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-blue-600 dark:text-blue-400">
                        {totals.total_matches}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-yellow-700 dark:text-yellow-300">
                        {totals.applied_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-green-700 dark:text-green-300">
                        {totals.favorited_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-indigo-700 dark:text-indigo-300">
                        {totals.overlap_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-purple-700 dark:text-purple-300">
                        {totals.potential_count}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {sortedStats.map((company, index) => (
                  <div key={index} className="p-4">
                    <div className="font-medium text-gray-900 dark:text-white mb-2">
                      {company.company_name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      {company.recruiter_count} recruiter{company.recruiter_count !== 1 ? 's' : ''}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Total Matches:</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">{company.total_matches}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Applied:</span>
                        <span className="font-semibold text-yellow-600 dark:text-yellow-400">{company.applied_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Favorited:</span>
                        <span className="font-semibold text-green-600 dark:text-green-400">{company.favorited_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Overlap:</span>
                        <span className="font-semibold text-indigo-600 dark:text-indigo-400">{company.overlap_count || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Potential:</span>
                        <span className="font-semibold text-purple-600 dark:text-purple-400">{company.potential_count}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Mobile Totals */}
                <div className="p-4 bg-gray-100 dark:bg-gray-700">
                  <div className="font-semibold text-gray-900 dark:text-white mb-3">
                    TOTAL ({sortedStats.length} companies)
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Total Matches:</span>
                      <span className="font-semibold text-blue-600 dark:text-blue-400">{totals.total_matches}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Applied:</span>
                      <span className="font-semibold text-yellow-600 dark:text-yellow-400">{totals.applied_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Favorited:</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">{totals.favorited_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Overlap:</span>
                      <span className="font-semibold text-indigo-600 dark:text-indigo-400">{totals.overlap_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Potential:</span>
                      <span className="font-semibold text-purple-600 dark:text-purple-400">{totals.potential_count}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Legend/Help Section */}
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Column Definitions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <span className="font-medium text-blue-600 dark:text-blue-400">Total Matches:</span> Unique students matching at least one recruiter's criteria at this company
              </div>
              <div>
                <span className="font-medium text-yellow-600 dark:text-yellow-400">Applied:</span> Students who submitted a NACME profile update (resume, transcript, etc.) this cycle AND match criteria
              </div>
              <div>
                <span className="font-medium text-green-600 dark:text-green-400">Favorited:</span> Students who favorited at least one recruiter at this company AND match criteria
              </div>
              <div>
                <span className="font-medium text-indigo-600 dark:text-indigo-400">Overlap:</span> Students who both applied AND favorited this company
              </div>
              <div>
                <span className="font-medium text-purple-600 dark:text-purple-400">Potential:</span> Students who match but have neither applied nor favorited (anonymous to recruiters)
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-500">
              Note: Applied and Favorited can overlap (a student can do both). The Overlap column shows this intersection.
            </p>
          </div>
        </div>
      </div>
    </AdminRouteGuard>
  );
};

export default AdminCompanyDashboardStats;
